import * as echarts from "echarts";
import {
  ChartAdapter,
  ChartBuildContext,
  EChartsOption,
  GeoHeatmapData,
  GeoHeatmapSettings,
} from "../types";

import {
  buildLegend,
  buildTooltip,
  buildVisualMap,
  mergeGeoHeatmapSettings,
} from "./shared";

const DEFAULT_GEO_MAP_NAME = "simple-geo";

// 简单的默认地图数据（如果项目中没有提供地图文件）
const DEFAULT_SIMPLE_GEO_JSON = {
  type: "FeatureCollection",
  features: [],
};

// 尝试动态导入地图文件，如果不存在则使用默认值
let simpleGeoJson: any = DEFAULT_SIMPLE_GEO_JSON;
// 注意：地图文件是可选的，如果项目中没有提供，使用默认空地图
// 这样可以避免构建时找不到文件的错误

const MAP_RESOURCES: Record<string, any> = {
  [DEFAULT_GEO_MAP_NAME]: simpleGeoJson,
};

function ensureMapRegistered(mapName?: string) {
  const targetName =
    mapName && MAP_RESOURCES[mapName] ? mapName : DEFAULT_GEO_MAP_NAME;
  if (!echarts.getMap(targetName)) {
    const resource = MAP_RESOURCES[targetName];
    if (resource) {
      echarts.registerMap(targetName, resource as any);
    }
  }
  return targetName;
}

function getGeoValueStats(points: GeoHeatmapData["points"]) {
  let min: number | undefined;
  let max: number | undefined;

  points.forEach((point) => {
    if (min === undefined || point.value < min) {
      min = point.value;
    }
    if (max === undefined || point.value > max) {
      max = point.value;
    }
  });

  return { min, max };
}

const geoHeatmapAdapter: ChartAdapter<"geoHeatmap"> = {
  type: "geoHeatmap",
  buildOption: (ctx: ChartBuildContext<"geoHeatmap">) => {
    const { data } = ctx;
    const mergedSettings = mergeGeoHeatmapSettings(ctx.settings);
    const requestedMapName = mergedSettings.geo?.map ?? data.geo?.map;
    const mapName = ensureMapRegistered(requestedMapName);
    const geoConfig = {
      map: mapName,
      roam:
        mergedSettings.geo?.roam ??
          data.geo?.roam ??
          MAP_RESOURCES[mapName] ? true : true,
      zoom: mergedSettings.geo?.zoom ?? data.geo?.zoom ?? 1,
    };

    const { min, max } = getGeoValueStats(data.points);

    const legend = buildLegend(data.legend, mergedSettings.legend);
    const tooltip = buildTooltip(data.tooltip, {
      trigger: mergedSettings.tooltip?.trigger ?? "item",
      enable: mergedSettings.tooltip?.enable,
      formatter: mergedSettings.tooltip?.formatter,
    });

    const visualMap = buildVisualMap(
      data.visualMap,
      mergedSettings.visualMap,
      { min, max }
    );

    const series = [
      {
        type: "heatmap",
        coordinateSystem: "geo",
        data: data.points.map((point) => ({
          value: [point.lng, point.lat, point.value],
          name: point.name,
        })),
        pointSize: 5,
        blurSize: 6,
      },
    ];

    const option: EChartsOption = {
      geo: {
        map: mapName,
        roam: geoConfig.roam ?? true,
        zoom: geoConfig.zoom ?? 1,
        silent: false,
        label: { show: false },
        itemStyle: {
          borderColor: "#fff",
          areaColor: "#f2f2f2",
        },
        emphasis: {
          label: { show: false },
          itemStyle: {
            areaColor: "#d3dffd",
          },
        },
      },
      legend,
      tooltip,
      series,
    };

    if (visualMap) {
      option.visualMap = {
        type: "continuous",
        orient: "vertical",
        left: "left",
        bottom: 20,
        ...visualMap,
      };
    }

    return option;
  },
  getDefaultSettings: (): Partial<GeoHeatmapSettings> => ({
    legend: { show: false },
    tooltip: { enable: true, trigger: "item" },
    visualMap: { show: true, calculable: true },
    geo: { map: DEFAULT_GEO_MAP_NAME, roam: true, zoom: 1 },
  }),
};

export default geoHeatmapAdapter;
